import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { Spinner } from '@/components/ui/spinner';
import { RequireAuth, RequireGuest, SessionGate } from '@/features/auth';
import { AuthLayout } from '@/layouts/AuthLayout';
import { RootLayout } from '@/layouts/RootLayout';

import { ROUTE_PATHS } from './paths';
import { RouteErrorBoundary } from './RouteErrorBoundary';

/*
 * Pages are lazily imported so each becomes its own chunk. Only the shell —
 * layouts, store, and router — is in the initial bundle; a visitor who lands
 * on the login page never downloads the dashboard's code.
 *
 * These are the one sanctioned place that reaches past a feature's public
 * barrel. Pages are deliberately absent from the barrels: re-exporting them
 * would pull every page into whatever bundle imports the barrel, defeating the
 * splitting entirely. The dependency still points one way — the router knows
 * about features, and no feature knows about the router.
 *
 * The authenticated shell is lazy for the same reason. It pulls in the
 * header's dropdown menus and their floating-ui dependency (~120 kB), which a
 * visitor landing on the login screen would otherwise download without ever
 * rendering.
 */
const AppLayout = lazy(() => import('@/layouts/AppLayout').then((m) => ({ default: m.AppLayout })));

const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const AccountManagementPage = lazy(() => import('@/features/accounts/pages/AccountManagementPage'));
const BillingPage = lazy(() => import('@/features/billing/pages/BillingPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'));

/**
 * Suspense wrapper for a lazy route.
 *
 * The fallback fills the viewport so the transition between routes does not
 * collapse the layout and shift content.
 */
function LazyRoute({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-64 items-center justify-center p-8">
          <Spinner size="lg" label="Loading page" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const routes: RouteObject[] = [
  {
    element: (
      // The session is resolved once, above every route, so a refresh on a
      // protected page does not bounce the user to login mid-probe.
      <SessionGate>
        <RootLayout />
      </SessionGate>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      // Guest-only routes.
      {
        element: <RequireGuest />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: ROUTE_PATHS.login,
                element: (
                  <LazyRoute>
                    <LoginPage />
                  </LazyRoute>
                ),
                handle: { title: 'Sign in' },
              },
            ],
          },
        ],
      },

      // Authenticated routes. The guard is applied once here rather than
      // repeated on each page.
      {
        element: <RequireAuth />,
        children: [
          {
            element: (
              <LazyRoute>
                <AppLayout />
              </LazyRoute>
            ),
            children: [
              { index: true, element: <Navigate to={ROUTE_PATHS.dashboard} replace /> },
              {
                path: ROUTE_PATHS.dashboard,
                element: (
                  <LazyRoute>
                    <DashboardPage />
                  </LazyRoute>
                ),
                handle: { title: 'Agency Overview' },
              },
              {
                path: ROUTE_PATHS.accounts,
                element: (
                  <LazyRoute>
                    <AccountManagementPage />
                  </LazyRoute>
                ),
                handle: { title: 'Sub-Accounts' },
              },
              {
                path: ROUTE_PATHS.billing,
                element: (
                  <LazyRoute>
                    <BillingPage />
                  </LazyRoute>
                ),
                handle: { title: 'Billing and invoices' },
              },
              {
                path: ROUTE_PATHS.settings,
                element: (
                  <LazyRoute>
                    <SettingsPage />
                  </LazyRoute>
                ),
                handle: { title: 'Settings' },
              },
            ],
          },
        ],
      },

      {
        path: ROUTE_PATHS.forbidden,
        element: (
          <LazyRoute>
            <ForbiddenPage />
          </LazyRoute>
        ),
        handle: { title: 'Access denied' },
      },

      // Catch-all. Must stay last.
      {
        path: ROUTE_PATHS.notFound,
        element: (
          <LazyRoute>
            <NotFoundPage />
          </LazyRoute>
        ),
        handle: { title: 'Page not found' },
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
export { routes };
